<script>
import * as echarts from 'echarts'

const validNumber = value => typeof value === 'number' && Number.isFinite(value)

export default {
  name: 'IndustryOperationChart',
  props: {
    chartData: { type: Object, default: null },
    compact: { type: Boolean, default: false }
  },
  computed: {
    hasValues() {
      return this.chartData && [...(this.chartData.leftData || []), ...(this.chartData.rightData || [])]
        .some(validNumber)
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() { this.renderChart() }
    },
    compact() { this.renderChart() }
  },
  mounted() {
    this._chart = echarts.init(this.$refs.chart)
    window.addEventListener('resize', this.resizeChart)
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(this.resizeChart)
      this._resizeObserver.observe(this.$refs.chart)
    }
    this.renderChart()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this._resizeObserver) this._resizeObserver.disconnect()
    if (this._chart) this._chart.dispose()
    this._chart = null
  },
  methods: {
    resizeChart() {
      if (this._chart) this._chart.resize()
    },
    renderChart() {
      if (!this._chart) return
      const pageData = this.chartData
      if (!pageData || !Array.isArray(pageData.xData) || !pageData.xData.length) {
        this._chart.clear()
        return
      }
      const leftData = pageData.xData.map((_, index) => validNumber(pageData.leftData[index]) ? pageData.leftData[index] : null)
      const rightData = pageData.xData.map((_, index) => validNumber(pageData.rightData[index]) ? pageData.rightData[index] : null)
      const axisStyle = {
        type: 'value',
        nameGap: 18,
        nameTextStyle: { color: '#E7FCFF', fontSize: 10 },
        axisLabel: { color: '#E7FCFF', fontSize: 10 },
        axisLine: { show: true, lineStyle: { color: 'rgba(239,247,253,.15)' } },
        splitLine: { lineStyle: { color: 'rgba(239,247,253,.1)' } }
      }
      this._chart.setOption({
        aria: { enabled: true },
        animationDuration: 400,
        legend: {
          top: 0,
          left: 'center',
          itemWidth: 14,
          itemHeight: 8,
          itemGap: 18,
          textStyle: { color: '#E7FCFF', fontSize: 11 },
          data: [pageData.leftTitle, pageData.rightTitle]
        },
        grid: { top: this.compact ? 52 : 58, left: 8, right: 8, bottom: 6, containLabel: true },
        tooltip: {
          trigger: 'axis',
          renderMode: 'richText',
          confine: true,
          axisPointer: { type: 'shadow' },
          formatter: params => {
            if (!params.length) return ''
            const index = params[0].dataIndex
            const format = value => validNumber(value) ? String(value) : '暂无数据'
            return `${pageData.xData[index]}\n${pageData.leftTitle}：${format(leftData[index])}\n${pageData.rightTitle}：${format(rightData[index])}`
          }
        },
        xAxis: {
          type: 'category',
          data: pageData.xData,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: 'rgba(239,247,253,.15)' } },
          axisLabel: {
            interval: 0,
            color: '#E7FCFF',
            fontSize: this.compact ? 11 : 10,
            lineHeight: 14,
            formatter: value => this.compact
              ? String(value).replace(/^\d{4}年/, '')
              : String(value).replace('年', '年\n')
          }
        },
        yAxis: [
          { ...axisStyle, name: pageData.leftTitle, nameTextStyle: { ...axisStyle.nameTextStyle, align: 'left' } },
          { ...axisStyle, name: pageData.rightTitle, nameTextStyle: { ...axisStyle.nameTextStyle, align: 'right' }, splitLine: { show: false } }
        ],
        series: [
          {
            name: pageData.leftTitle,
            type: 'custom',
            itemStyle: { color: '#1A78E6' },
            encode: { x: 0, y: 1 },
            data: leftData.map((value, index) => [index, value]),
            renderItem: (params, api) => {
              // 缺失月份不绘制柱体；null 不能转换为零。
              if (!validNumber(leftData[params.dataIndex])) return null
              const point = api.coord([api.value(0), api.value(1)])
              const baseline = api.coord([api.value(0), 0])
              const halfWidth = Math.min(8, api.size([1, 0])[0] / 4)
              const depth = halfWidth / 2
              const gradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#2797EF' },
                { offset: 1, color: '#0F427C' }
              ])
              return {
                type: 'group',
                children: [
                  { type: 'polygon', shape: { points: [[point[0], point[1]], [point[0] - halfWidth, point[1] - depth], [baseline[0] - halfWidth, baseline[1] - depth], baseline] }, style: { fill: gradient } },
                  { type: 'polygon', shape: { points: [point, baseline, [baseline[0] + halfWidth, baseline[1] - depth], [point[0] + halfWidth, point[1] - depth]] }, style: { fill: '#1A67AC' } },
                  { type: 'polygon', shape: { points: [point, [point[0] + halfWidth, point[1] - depth], [point[0], point[1] - depth * 2], [point[0] - halfWidth, point[1] - depth]] }, style: { fill: '#B3E9FF' } }
                ]
              }
            }
          },
          {
            name: pageData.rightTitle,
            type: 'line',
            yAxisIndex: 1,
            data: rightData,
            connectNulls: false,
            smooth: true,
            symbol: 'circle',
            symbolSize: 7,
            itemStyle: { color: '#01EEFD' },
            lineStyle: { width: 2 },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(67,239,255,.2)' },
                { offset: 1, color: 'rgba(1,17,49,.05)' }
              ])
            }
          }
        ]
      }, true)
    }
  }
}
</script>

<template>
  <div class="operation-chart">
    <div ref="chart" class="operation-chart-canvas"></div>
    <div v-if="!hasValues" class="operation-chart-empty">暂无数据</div>
  </div>
</template>

<style scoped>
.operation-chart { position: relative; width: 100%; height: 100%; min-height: 180px; }
.operation-chart-canvas { width: 100%; height: 100%; }
.operation-chart-empty { position: absolute; top: 50%; width: 100%; color: #B5C5D4; text-align: center; font-size: 13px; }
</style>
